import { FC, MouseEvent } from 'react';
import { ArrowRight, Pencil, Target } from 'lucide-react';
import AttentionOverlay from '../common/AttentionOverlay';
import { useClassFocusOnboarding } from '../../contexts/ClassFocusOnboardingContext';

interface Props {
  hasActiveFocus: boolean;
  disabled?: boolean;
  /** Disables only the primary (Set/Change focus) button — e.g. an active focus with nowhere to move. */
  primaryDisabled?: boolean;
  editActive?: boolean;
  onPrimaryClick: (e: MouseEvent) => void;
  onEditClick: (e: MouseEvent) => void;
}

export const UnitFocusActions: FC<Props> = ({
  hasActiveFocus,
  disabled = false,
  primaryDisabled = false,
  editActive = false,
  onPrimaryClick,
  onEditClick,
}) => {
  const { pulsing, coachmarkVisible, dismissCoachmark } = useClassFocusOnboarding();
  const isPrimaryDisabled = disabled || primaryDisabled;
  const showAttention = pulsing && !hasActiveFocus;

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-shrink-0">
      {hasActiveFocus && (
        <button
          type="button"
          aria-label="Edit focus"
          aria-expanded={editActive}
          className={`tw-inline-flex tw-items-center tw-justify-center tw-w-11 tw-h-11 tw-p-0 tw-border-2 tw-border-indigo-600 tw-rounded-full tw-shrink-0 ${
            editActive ? 'tw-bg-indigo-600 tw-text-white' : 'tw-bg-white tw-text-indigo-600'
          } ${disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'}`}
          onClick={onEditClick}
          disabled={disabled}
        >
          <Pencil size={20} aria-hidden />
        </button>
      )}
      <div className="tw-relative">
        <button
          type="button"
          className={`tw-inline-flex tw-justify-center tw-items-center tw-w-72 tw-gap-3 tw-py-3 tw-px-6 tw-border-solid tw-border-2 tw-border-indigo-600 tw-rounded-full tw-font-bold tw-text-base tw-leading-5 tw-whitespace-nowrap ${
            hasActiveFocus ? 'tw-bg-white tw-text-indigo-600' : 'tw-bg-indigo-600 tw-text-white'
          } ${isPrimaryDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'} ${
            showAttention ? 'nc_pulse-attention' : ''
          } `}
          onClick={onPrimaryClick}
          disabled={isPrimaryDisabled}
        >
          {hasActiveFocus ? (
            <>
              <Target size={20} aria-hidden />
              Change focus
            </>
          ) : (
            <>
              <Target aria-hidden />
              Set focus
              <ArrowRight size={20} aria-hidden />
            </>
          )}
        </button>
        {coachmarkVisible && !hasActiveFocus && (
          <AttentionOverlay
            title="Pick your starting unit, then set focus"
            message="Ensure Roadmaps aligns with what’s being covered in class."
            onClose={dismissCoachmark}
            className="!tw-z-[2]"
          />
        )}
      </div>
    </div>
  );
};
